/* Shared card action: one option adds; multiple options require an explicit
   choice in a native dialog. Capture delegation also covers filtered cards. */
(() => {
  'use strict';
  const config = document.currentScript?.dataset || {};
  const selector = '.avora-card--shop__add, .avora-card__quick-add';
  const cardSelector = '.avora-card--shop, .avora-card';
  let dialog, trigger, controller, product, selects = [], chosen, submitting = false;
  let previousOverflow = '', noticeTimer;
  const path = suffix => ((window.Shopify?.routes?.root || '/').replace(/\/$/, '') + '/' + suffix);
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  };
  // A compare-at price means Shopify's price is already the sale price.
  const money = (cents, compareAt) => {
    const percentage = config.saleEnabled === 'true' && !(Number(compareAt) > cents) ? Number(config.salePercentage) || 0 : 0;
    const price = cents - Math.floor(cents * percentage / 100);
    const localizer = window.AvoraMarketLocalization;
    const context = localizer?.context(window);
    if (context?.ready) return localizer.formatMinor(price, context.currency, context.locale);
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: window.Shopify?.currency?.active || config.currency || 'EUR'
    }).format(price / 100);
  };
  function productLink(button) {
    const card = button.closest(cardSelector);
    // Live swatches update the detail URL with the explicitly previewed variant.
    return card?.querySelector('a[href*="/products/"]')?.href || button.dataset.productUrl || button.href;
  }
  function status(button, message, error = false) {
    const card = button.closest(cardSelector);
    if (!card) return;
    let el = card.querySelector('[data-avora-action-status]');
    if (!el) {
      el = node('p', 'avora-card-action-status');
      el.dataset.avoraActionStatus = '';
      el.setAttribute('role', 'status');
      card.append(el);
    }
    el.classList.toggle('is-error', error);
    el.textContent = message;
  }
  function busy(button, value) {
    button.disabled = value;
    button.classList.toggle('is-loading', value);
    if (value) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
  async function request(url, options) {
    const response = await fetch(url, options);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || window.AvoraI18n.text('Could not add this item. Please try again.'));
    return data;
  }
  function notice(title) {
    let el = document.querySelector('.avora-card-action-notice');
    if (!el) {
      el = node('div', 'avora-card-action-notice');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.append(el);
    }
    const link = node('a', '', window.AvoraI18n.text('View cart'));
    link.href = path('cart');
    el.replaceChildren(node('span', '', title + window.AvoraI18n.text(' added to cart.')), link);
    el.hidden = false;
    clearTimeout(noticeTimer);
    noticeTimer = setTimeout(() => { el.hidden = true; }, 5000);
  }
  async function add(button, variantId, quantity, title) {
    busy(button, true);
    status(button, window.AvoraI18n.text('Adding to cart…'));
    try {
      const payload = await request(path('cart/add.js'), {
        method: 'POST', credentials: 'same-origin',
        headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
        body: JSON.stringify({items: [{id: Number(variantId), quantity}]})
      });
      // Adding succeeded even if a later cart refresh fails. Never encourage
      // retrying a successful mutation because a secondary GET was unavailable.
      if (dialog?.open) dialog.close();
      button.classList.add('is-added', 'success');
      status(button, title + window.AvoraI18n.text(' added to cart.'));
      notice(title);
      document.dispatchEvent(new CustomEvent('cart:update', {bubbles: true, detail: {source: 'card-actions', payload}}));
      fetch(path('cart.js')).then(r => r.json()).then(cart => {
        document.querySelectorAll('.cart-count-badge, [data-cart-count], .cart-bubble__text-count').forEach(el => {
          el.textContent = cart.item_count;
          el.closest('.cart-bubble')?.classList.remove('visually-hidden');
        });
      }).catch(() => {});
      setTimeout(() => { button.classList.remove('is-added', 'success'); busy(button, false); }, 1500);
      return true;
    } catch (error) {
      busy(button, false);
      status(button, error.message, true);
      const feedback = dialog?.open && dialog.querySelector('[data-picker-status]');
      if (feedback) feedback.textContent = error.message;
      return false;
    }
  }
  function createDialog() {
    dialog = node('dialog', 'avora-card-picker');
    dialog.setAttribute('aria-labelledby', 'avora-card-picker-title');
    document.body.append(dialog);
    dialog.addEventListener('cancel', event => { if (submitting) event.preventDefault(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog || submitting) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      controller?.abort();
      document.documentElement.style.overflow = previousOverflow;
      const oldTrigger = trigger;
      if (oldTrigger?.isConnected) oldTrigger.focus({preventScroll: true});
      product = null;
      chosen = null;
    });
  }
  function refreshChoice() {
    const values = selects.map(select => select.value);
    // Disable impossible/sold-out choices using earlier selections; later
    // selections reset when a parent changes, so no combination traps the user.
    selects.forEach((select, index) => {
      for (const option of [...select.options].slice(1)) {
        option.disabled = !product.variants.some(v => v.available && v.options[index] === option.value &&
          values.slice(0, index).every((value, i) => !value || v.options[i] === value));
      }
    });
    chosen = product.variants.find(v => v.available && !v.requires_selling_plan && v.options.every((value, i) => values[i] === value));
    const submit = dialog.querySelector('[data-picker-add]');
    submit.disabled = !chosen || submitting;
    submit.textContent = chosen ? window.AvoraI18n.text('Add to cart · ') + money(chosen.price, chosen.compare_at_price) : window.AvoraI18n.text('Choose options');
    dialog.querySelector('[data-picker-status]').textContent = chosen ? '' : window.AvoraI18n.text('Choose each option to continue.');
    const preview = chosen || product.variants.find(v => v.available && values.some(Boolean) && v.options.every((value, i) => !values[i] || values[i] === value));
    const image = dialog.querySelector('img');
    if (preview?.featured_image?.src) image.src = preview.featured_image.src;
    dialog.querySelector('[data-picker-price]').textContent = chosen ? money(chosen.price, chosen.compare_at_price) : (product.price_varies ? 'From ' : '') + money(product.price_min, product.compare_at_price_min);
  }
  async function openPicker(button) {
    if (!dialog) createDialog();
    if (dialog.open) return;
    trigger = button;
    submitting = false;
    chosen = null;
    controller = new AbortController();
    const url = new URL(productLink(button), location.origin);
    if (url.origin !== location.origin) return;
    const close = node('button', 'avora-card-picker__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', window.AvoraI18n.text('Close product options'));
    close.addEventListener('click', () => { if (!submitting) dialog.close(); });
    const title = node('h2', '', window.AvoraI18n.text('Choose your options'));
    title.id = 'avora-card-picker-title';
    const description = node('p', 'avora-card-picker__hint', window.AvoraI18n.text('Choose your finish or size, then add it to your cart.'));
    const loading = node('p', '', window.AvoraI18n.text('Loading product options…'));
    loading.setAttribute('role', 'status');
    const details = node('a', 'avora-card-picker__details', window.AvoraI18n.text('View full product details'));
    details.href = url.pathname;
    dialog.replaceChildren(close, title, description, loading, details);
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    dialog.showModal();
    close.focus();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const result = await request(url.pathname.replace(/\/$/, '') + '.js', {signal: controller.signal});
      if (!dialog.open) return;
      if (!Array.isArray(result.variants) || !result.options?.length) throw new Error(window.AvoraI18n.text('Options are unavailable. Open the product details to continue.'));
      product = result;
      if (product.variants.length === 1 && product.variants[0].available && !product.variants[0].requires_selling_plan) {
        const only = product.variants[0];
        submitting = true;
        close.disabled = true;
        // Even a named single variant needs no extra decision from the shopper.
        const success = await add(button, only.id, Math.max(1, Number(only.quantity_rule?.min) || 1), product.title);
        submitting = false;
        if (success) return;
        close.disabled = false;
      }
      loading.remove();
      description.textContent = product.title;
      const overview = node('div', 'avora-card-picker__overview');
      const image = node('img');
      image.src = product.featured_image || '';
      image.alt = product.title;
      image.width = 96; image.height = 96;
      const price = node('strong'); price.dataset.pickerPrice = '';
      overview.append(image, price);
      const form = node('form', 'avora-card-picker__form');
      selects = product.options.map((option, index) => {
        const label = node('label', '', option.name || option);
        const select = node('select');
        select.required = true;
        select.setAttribute('aria-label', option.name || option);
        select.append(new Option('Choose ' + String(option.name || option).toLowerCase(), ''));
        [...new Set(product.variants.map(v => v.options[index]))].forEach(value => select.append(new Option(value, value)));
        select.addEventListener('change', () => {
          selects.slice(index + 1).forEach(s => { s.value = ''; });
          refreshChoice();
        });
        label.append(select); form.append(label);
        return select;
      });
      const feedback = node('p', 'avora-card-picker__status');
      feedback.dataset.pickerStatus = ''; feedback.setAttribute('role', 'status');
      const submit = node('button', 'avora-card-picker__submit', window.AvoraI18n.text('Choose options'));
      submit.type = 'submit'; submit.dataset.pickerAdd = '';
      form.append(feedback, submit);
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (!chosen || submitting) return;
        submitting = true;
        const variant = chosen;
        const quantity = Math.max(1, Number(variant.quantity_rule?.min) || 1);
        submit.disabled = true; submit.textContent = window.AvoraI18n.text('Adding…');
        submit.setAttribute('aria-busy', 'true');
        close.disabled = true;
        selects.forEach(s => { s.disabled = true; });
        const success = await add(button, variant.id, quantity, product.title);
        submitting = false;
        if (!success && dialog.open) {
          close.disabled = false;
          selects.forEach(s => { s.disabled = false; });
          submit.disabled = false; submit.removeAttribute('aria-busy');
          submit.textContent = window.AvoraI18n.text('Try again · ') + money(variant.price, variant.compare_at_price);
        }
      });
      details.before(overview, form);
      // An explicitly clicked swatch carries its variant in the product URL.
      const initial = product.variants.find(v => String(v.id) === url.searchParams.get('variant') && v.available);
      if (initial) selects.forEach((s, i) => { s.value = initial.options[i]; });
      refreshChoice();
      selects[0]?.focus();
    } catch (error) {
      if (dialog.open) loading.textContent = error.name === 'AbortError' ? window.AvoraI18n.text('Options took too long to load. Close and try again, or view the product details.') : error.message;
    } finally { clearTimeout(timeout); }
  }
  document.addEventListener('click', event => {
    const button = event.target.closest?.(selector);
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (button.disabled || button.classList.contains('is-loading')) return;
    const multiple = button.dataset.hasVariants === 'true' || button.dataset.hasOnlyDefault === 'false' || button.tagName === 'A';
    if (multiple) { openPicker(button); return; }
    if (!button.dataset.variantId) return;
    const title = button.dataset.productTitle || button.closest(cardSelector)?.querySelector('h3')?.textContent || 'Item';
    add(button, button.dataset.variantId, Math.max(1, Number(button.dataset.quantity) || 1), title);
  }, true);
})();
